import moment from 'moment'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate, useParams} from 'react-router'
import {Visit} from 'entities/activity'
import {
  clearProfile,
  clearStateGallery,
  GetUserInfo,
  profileMediaModel,
  setProfile,
  useOpenStateGallery,
  useProfile,
  useProfileMediaIsLoaded
} from 'entities/profile'
import {useUser} from 'entities/user'
import {Interests} from 'features/interests'
import {upgradeHandler} from 'features/payment/lib'
import {WarningModalProvider} from 'features/warningModal/context'
import {useScroll} from 'shared/hooks'
import {mediaVariants, names, skipFalsy} from 'shared/lib'
import {scrollToTop} from 'shared/lib/scroll-to'
import {UserStatus} from 'shared/types'
import {Avatar} from 'shared/ui/avatar'
import {Button} from 'shared/ui/buttons'
import {ArrowLeft} from 'shared/ui/icons'
import {Modal} from 'shared/ui/modal'
import {useReactivate} from '../../hooks'
import {MyMedia} from '../myMedia'
import {ProfileAboutMe} from '../profileAboutMe'
import {ProfileBanner} from '../profileBanner'
import {ProfileMedia} from '../profileMedia'
import {ProfileSummary} from '../profileSummary'
import {SimilarProfiles} from '../similarProfiles'
import styles from './styles.module.scss'

export const ProfileMobile: FC = () => {
  const dispatch = useDispatch()
  const {isLoading, reactivateHandler} = useReactivate()
  const {id: idUser} = useParams()
  const [ref, setRef] = useState<HTMLDivElement | null>(null) 
  const user = useUser()
  const navigate = useNavigate()
  const profile = useProfile()
  const {name, avatarUrl, videoAvatarUrl, interests} = idUser ? profile : user.profile
  const age = idUser ? profile.age : moment().diff(user.profile.dateBirth, 'years')
  const [isScrolled, setIsScrolled] = useState(false)
  const scroll = useScroll(ref)
  const mediaIsMounted = useProfileMediaIsLoaded()
  const [currentUserId, setCurrentUserId] = useState(idUser)
  const isOpenGallery = useOpenStateGallery()

  useEffect(() => {
    setIsScrolled(scroll > 50)
  }, [scroll])

  useEffect(() => {
    if (!idUser) return

    setCurrentUserId(idUser)

    return () => {
      dispatch(profileMediaModel.actions.setIsMediaLoaded(false))
    }
  }, [idUser])

  useEffect(() => {
    if (!ref || !idUser) return

    setTimeout(() => scrollToTop(ref))
    dispatch(clearStateGallery())

    GetUserInfo(idUser)
      .then(({data}) => dispatch(setProfile(data)))
      .catch(console.error)

    Visit(idUser).catch(console.error)

    return () => {
      dispatch(clearProfile())
    }
  }, [idUser, ref])

  const chatNowHandler = () => {
    navigate(`/messages/${idUser}`)
  }

  const onCloseHandler = () => {
    const historyState = window.history.state

    if (historyState && historyState.idx > 0) {
      return navigate(-1)
    }
    navigate('/', {replace: true})
  }
  const isActive = useMemo(() => user.status === UserStatus.ACTIVE, [user.status])

  const BottomButton = useCallback(
    () => {
      if (idUser) {
        return (
          <Button
            as='button'
            styleType='primary'
            onClick={chatNowHandler}
            className={styles.btnChat}
            fullwidth
          >
            Chat now
          </Button>
        )
      }

      if (isActive) {
        return (
          <Button
            as='button'
            styleType='primary'
            onClick={() => upgradeHandler({dispatch, paymentsCount: user.paymentsCount})}
            className={styles.btnPremium}
          >
            {user.paymentsCount > 0 ? 'Refill credits':' Upgrade account'}
          </Button>
        )
      }

      return (
        <Button
          as='button'
          styleType='primary'
          onClick={reactivateHandler}
          fullwidth
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Reactivate account
        </Button>
      )
    },
    [isActive, idUser]
  )

  return (
    <WarningModalProvider>
      <Modal
        disableEnforceFocus
        open
        className={styles.modal}
        classes={{backdrop: styles.modalBackdrop, root: names(styles.modalRoot, isOpenGallery && styles.galleryActive)}}
      >
        <div className={styles.mobileProfile}>
          <div className={names(styles.mobileProfileHeaderWrapper, isScrolled ? styles.active : '')}>
            <div className={styles.header}>
              <div
                className={styles.headerBackBtn}
                onClick={onCloseHandler}
              >
                <ArrowLeft />
              </div>
              <Avatar
                sx={{width: 40, height: 40}}
                src={videoAvatarUrl || avatarUrl + mediaVariants.square256 || ''}
                alt={name}
                className={styles.item}
              />
              <h2 className={styles.headerTitle}>{skipFalsy(name, String(age || '')).join(', ')}</h2>
            </div>
          </div>
          <div className={names(styles.content, styles.customScroll)} ref={setRef}>
            <ProfileBanner />
            <ProfileSummary />
            <ProfileAboutMe />
            {interests.length
              ? <div className={styles.interests}>
                <Interests data={interests} />
              </div>
              : null}
            {idUser ?
              <>
                <ProfileMedia wrapperRef={ref} />
                {currentUserId === idUser && mediaIsMounted ? <SimilarProfiles /> : null}
              </>
              :
              <MyMedia wrapperRef={ref} />
            }
            <div className={styles.bottom}>
              <BottomButton />
            </div>
          </div>
        </div>
      </Modal>
    </WarningModalProvider>
  )
}
